import React, { createContext, useContext, useEffect } from 'react';

interface HeaderActionsCtx {
  setActions: (node: React.ReactNode) => void;
}

export const HeaderActionsContext = createContext<HeaderActionsCtx>({
  setActions: () => {},
});

/**
 * Pages call this hook to project a primary action (e.g. "New product")
 * into the admin header's action slot. The action is cleared on unmount.
 *
 * Usage inside a page:
 *   useHeaderActions(<Button type="primary">Новий товар</Button>);
 *
 * Slot contract (keep the header scannable):
 *  - at most ONE primary Button, optionally one secondary — never 3+ buttons
 *  - never inputs/Selects/Segmented (filters live in the page body)
 *  - never dirty-state Save/Cancel pairs (they belong next to the form/table)
 * On <767px the slot renders icon-only via CSS — always pass an `icon`.
 */
export const useHeaderActions = (node: React.ReactNode): void => {
  const { setActions } = useContext(HeaderActionsContext);
  useEffect(() => {
    setActions(node);
    return () => setActions(null);
  }, [node, setActions]);
};
